import { arrayMove } from '@dnd-kit/sortable';

import type { EquipmentDataQueryCache } from '@entities/equipment';
import type { ShortInventoryDto } from '@entities/equipmentInventory';

const SORT_INDEX_FIELD = 'sort_index';

export const reorderWarehousesInEquipmentCache = ({
    previous,
    oldIndex,
    newIndex,
}: {
    previous: EquipmentDataQueryCache | undefined;
    oldIndex: number;
    newIndex: number;
}): EquipmentDataQueryCache | undefined => {
    if (!previous?.data.response.equipment) {
        return previous;
    }

    const reorderedWarehouses = arrayMove(previous.data.response.equipment, oldIndex, newIndex).map(
        (warehouse, index) => ({
            ...warehouse,
            [SORT_INDEX_FIELD]: index,
        })
    );

    return {
        ...previous,
        data: {
            ...previous.data,
            response: {
                ...previous.data.response,
                equipment: reorderedWarehouses,
            },
        },
    };
};

export const reorderInventoryInEquipmentCache = ({
    previous,
    inventoryGuid,
    fromWarehouseId,
    toWarehouseId,
    newIndex,
}: {
    previous: EquipmentDataQueryCache | undefined;
    inventoryGuid: string;
    fromWarehouseId: number;
    toWarehouseId: number;
    newIndex: number;
}): EquipmentDataQueryCache | undefined => {
    if (!previous?.data.response.equipment) {
        return previous;
    }

    const { equipment } = previous.data.response;
    const sourceWarehouse = equipment.find((warehouse) => warehouse.id === fromWarehouseId);
    const targetWarehouse = equipment.find((warehouse) => warehouse.id === toWarehouseId);
    const movedInventory = sourceWarehouse?.items.find((item) => item.guid === inventoryGuid);

    if (!sourceWarehouse || !targetWarehouse || !movedInventory) {
        return previous;
    }

    const equipmentWithoutMovedInventory = equipment.map((warehouse) => {
        if (warehouse.id !== fromWarehouseId) {
            return warehouse;
        }

        return {
            ...warehouse,
            items: warehouse.items
                .filter((item) => item.guid !== inventoryGuid)
                .map((item, index) => ({
                    ...item,
                    [SORT_INDEX_FIELD]: index,
                })),
        };
    });

    return {
        ...previous,
        data: {
            ...previous.data,
            response: {
                ...previous.data.response,
                equipment: equipmentWithoutMovedInventory.map((warehouse) => {
                    if (warehouse.id !== toWarehouseId) {
                        return warehouse;
                    }

                    const nextItems = [...warehouse.items];
                    const insertIndex = Math.min(Math.max(newIndex, 0), nextItems.length);
                    nextItems.splice(insertIndex, 0, movedInventory);

                    return {
                        ...warehouse,
                        items: nextItems.map((item, index) => ({
                            ...item,
                            [SORT_INDEX_FIELD]: index,
                        })),
                    };
                }),
            },
        },
    };
};

export const addMovedInventoryToEquipmentCache = ({
    previous,
    fromWarehouseId,
    warehouseId,
    inventoryGuid,
    inventory,
}: {
    previous: EquipmentDataQueryCache | undefined;
    fromWarehouseId?: number;
    warehouseId: number;
    inventoryGuid?: string;
    inventory: ShortInventoryDto;
}): EquipmentDataQueryCache | undefined => {
    if (!previous?.data.response.equipment) {
        return previous;
    }

    return {
        ...previous,
        data: {
            ...previous.data,
            response: {
                ...previous.data.response,
                equipment: previous.data.response.equipment.map((warehouseItem) => {
                    if (warehouseItem.id === fromWarehouseId) {
                        return {
                            ...warehouseItem,
                            items: warehouseItem.items
                                .filter((item) => item.guid !== inventoryGuid)
                                .map((item, index) => ({
                                    ...item,
                                    [SORT_INDEX_FIELD]: index,
                                })),
                        };
                    }

                    if (warehouseItem.id !== warehouseId) {
                        return warehouseItem;
                    }

                    const withoutDuplicate = warehouseItem.items.filter(
                        (item) => item.guid !== inventory.guid
                    );

                    return {
                        ...warehouseItem,
                        items: [...withoutDuplicate, inventory].map((item, index) => ({
                            ...item,
                            [SORT_INDEX_FIELD]: index,
                        })),
                    };
                }),
            },
        },
    };
};

export const partiallyMoveInventoryInEquipmentCache = ({
    previous,
    inventoryGuid,
    fromWarehouseId,
    toWarehouseId,
    newIndex,
    quantity,
    createdInventory,
}: {
    previous: EquipmentDataQueryCache | undefined;
    inventoryGuid: string;
    fromWarehouseId: number;
    toWarehouseId: number;
    newIndex: number;
    quantity: number;
    createdInventory: ShortInventoryDto;
}): EquipmentDataQueryCache | undefined => {
    if (!previous?.data.response.equipment) {
        return previous;
    }

    const sourceWarehouse = previous.data.response.equipment.find(
        (warehouse) => warehouse.id === fromWarehouseId
    );
    const sourceInventory = sourceWarehouse?.items.find((item) => item.guid === inventoryGuid);

    if (!sourceInventory) {
        return previous;
    }

    return {
        ...previous,
        data: {
            ...previous.data,
            response: {
                ...previous.data.response,
                equipment: previous.data.response.equipment.map((warehouse) => {
                    if (warehouse.id === fromWarehouseId) {
                        return {
                            ...warehouse,
                            items: warehouse.items.map((item) =>
                                item.guid === inventoryGuid
                                    ? {
                                          ...item,
                                          amount: Math.max(0, item.amount - quantity),
                                      }
                                    : item
                            ),
                        };
                    }

                    if (warehouse.id !== toWarehouseId) {
                        return warehouse;
                    }

                    const nextItems = warehouse.items.filter(
                        (item) => item.guid !== createdInventory.guid
                    );
                    const insertIndex = Math.min(Math.max(newIndex, 0), nextItems.length);

                    nextItems.splice(insertIndex, 0, createdInventory);

                    return {
                        ...warehouse,
                        items: nextItems.map((item, index) => ({
                            ...item,
                            [SORT_INDEX_FIELD]: index,
                        })),
                    };
                }),
            },
        },
    };
};
